import React, { useCallback, useMemo, useState, useEffect } from "react";
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import ServerPaginatedTable from "@Components/ServerPaginatedTable/ServerPaginatedTable";
import useSendGetRequest from "@Hooks/useSendGetRequest";

const EXIT_REQUEST_ACTION_LOGS_BASE = "/exit-request";
const MONTH_NAME_TO_NUMBER = {
  JANUARY: 1,
  FEBRUARY: 2,
  MARCH: 3,
  APRIL: 4,
  MAY: 5,
  JUNE: 6,
  JULY: 7,
  AUGUST: 8,
  SEPTEMBER: 9,
  OCTOBER: 10,
  NOVEMBER: 11,
  DECEMBER: 12,
};

const DEFAULT_SORT_MODEL = [
  {
    field: "timestamp",
    sort: "desc",
  },
];

const ExitRequestLogs = ({
  exitRequestId,
  meta = {},
  height = "60vh",
  minHeight = "320px",
  variant = "modal",
  showSummary = true,
  emptyStateText = "Select an exit request to view its logs.",
}) => {
  const { loading: logsLoading, getData: getActionLogs } = useSendGetRequest(
    true
  );
  const [logRows, setLogRows] = useState([]);
  const [logRowCount, setLogRowCount] = useState(0);

  useEffect(() => {
    setLogRows([]);
    setLogRowCount(0);
  }, [exitRequestId]);

  const parseBackendTimestamp = useCallback((value) => {
    if (value === null || value === undefined) {
      return null;
    }

    if (dayjs.isDayjs(value)) {
      return value;
    }

    if (value instanceof Date) {
      const parsedDate = dayjs(value);
      return parsedDate.isValid() ? parsedDate : null;
    }

    if (Array.isArray(value)) {
      const [
        year,
        month = 1,
        day = 1,
        hour = 0,
        minute = 0,
        second = 0,
        nano = 0,
      ] = value;
      if (typeof year === "number") {
        const date = dayjs(
          new Date(
            year,
            (month ?? 1) - 1,
            day ?? 1,
            hour ?? 0,
            minute ?? 0,
            second ?? 0,
            Math.floor((nano ?? 0) / 1_000_000)
          )
        );
        return date.isValid() ? date : null;
      }
    }

    if (typeof value === "object") {
      const {
        year,
        monthValue,
        month,
        dayOfMonth,
        day,
        hour,
        minute,
        second,
        nano,
      } = value;
      if (typeof year === "number") {
        const normalizedMonth =
          (typeof monthValue === "number" && monthValue > 0
            ? monthValue
            : undefined) ??
          (typeof month === "number" && month > 0 ? month : undefined) ??
          (typeof month === "string"
            ? MONTH_NAME_TO_NUMBER[month.toUpperCase()]
            : undefined) ??
          1;
        const normalizedDay =
          (typeof dayOfMonth === "number" && dayOfMonth > 0
            ? dayOfMonth
            : undefined) ??
          (typeof day === "number" && day > 0 ? day : undefined) ??
          1;

        const date = dayjs(
          new Date(
            year,
            normalizedMonth - 1,
            normalizedDay,
            hour ?? 0,
            minute ?? 0,
            second ?? 0,
            Math.floor((nano ?? 0) / 1_000_000)
          )
        );
        return date.isValid() ? date : null;
      }
    }

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }, []);

  const formatLogTimestamp = useCallback(
    (value) => {
      const parsedDate = parseBackendTimestamp(value);
      return parsedDate ? parsedDate.format("MMM DD, YYYY HH:mm") : "-";
    },
    [parseBackendTimestamp]
  );

  const renderLogCellContent = useCallback(
    (value) => (
      <Typography
        variant="body2"
        sx={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          width: "100%",
          lineHeight: 1.35,
        }}
      >
        {value ?? "-"}
      </Typography>
    ),
    []
  );

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: "timestamp",
        headerName: "Timestamp",
        flex: 1.4,
        sortable: false,
        renderCell: (params) =>
          renderLogCellContent(formatLogTimestamp(params.row?.timestamp)),
      },
      {
        field: "actionTypeDisplayName",
        headerName: "Action",
        flex: 1.4,
        renderCell: (params) =>
          renderLogCellContent(
            params.row?.actionTypeDisplayName ||
              params.row?.actionType ||
              "-"
          ),
      },
      {
        field: "performedByUsername",
        headerName: "Performed By",
        flex: 1,
        renderCell: (params) =>
          renderLogCellContent(params.row?.performedByUsername),
      },
      {
        field: "performedByEmail",
        headerName: "Email",
        flex: 1.4,
        renderCell: (params) =>
          renderLogCellContent(params.row?.performedByEmail),
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1.6,
        sortable: false,
        renderCell: (params) => renderLogCellContent(params.row?.description),
      },
    ];

    return baseColumns.map((column) => ({
      ...column,
      cellClassName: column.cellClassName
        ? `${column.cellClassName} logsWrapCell`
        : "logsWrapCell",
    }));
  }, [renderLogCellContent, formatLogTimestamp]);

  const fetchActionLogsPage = useCallback(
    (queryOptions) => {
      if (!exitRequestId) {
        return;
      }
      const params = {
        ...queryOptions,
        // Action logs have no sortable columns; always sort by timestamp. The
        // route-scoped sessionStorage may otherwise restore the listing page's
        // sort (e.g. numberOfRemainingResponses), which is invalid for this query.
        sortBy: "timestamp",
        sortDir: "desc",
      };

      getActionLogs(
        `${EXIT_REQUEST_ACTION_LOGS_BASE}/${exitRequestId}/action-logs/paginated`,
        params
      ).then((data) => {
        if (!data) {
          setLogRows([]);
          setLogRowCount(0);
          return;
        }
        setLogRows(data?.content ?? []);
        setLogRowCount(data?.totalElements ?? 0);
      });
    },
    [exitRequestId, getActionLogs]
  );

  const formatStatus = useCallback((status) => {
    if (!status) {
      return "-";
    }
    return status.replace(/_/g, " ");
  }, []);

  if (!exitRequestId) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {emptyStateText}
        </Typography>
      </Box>
    );
  }

  const cardElevation = variant === "tab" ? 0 : 1;
  const rowHeight = variant === "tab" ? 70 : 90;

  const logsTableProps = {
    columns,
    rows: logRows,
    rowCount: logRowCount,
    isLoading: logsLoading,
    fetchPage: fetchActionLogsPage,
    disableColumnMenu: true,
    disableRowSelectionOnClick: true,
    minHeight,
    height,
    getRowId: (row) =>
      row.id ?? `${row.timestamp}-${row.actionType}-${row.performedById}`,
    initialSortModel: DEFAULT_SORT_MODEL,
    className: `logsGrid logsEnhanced ${variant === "tab" ? "logsTab" : ""}`,
    rowHeight,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {showSummary && (
        <Box
          sx={{
            backgroundColor: "background.paper",
            borderRadius: 2,
            boxShadow: (theme) => theme.shadows[cardElevation],
            p: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            useFlexGap
            flexWrap="wrap"
          >
            {meta.status && (
              <Chip
                size="small"
                color="primary"
                label={`Status: ${formatStatus(meta.status)}`}
              />
            )}
            {exitRequestId && (
              <Chip
                size="small"
                color="secondary"
                label={`Request ID: ${exitRequestId}`}
              />
            )}
            {meta.userEmail && (
              <Chip
                size="small"
                variant="outlined"
                label={`Created By: ${meta.userEmail}`}
              />
            )}
            {meta.ingredientName && (
              <Chip
                size="small"
                variant="outlined"
                label={`Ingredient: ${meta.ingredientName}${
                  meta.casNumber ? ` (${meta.casNumber})` : ""
                }`}
              />
            )}
          </Stack>
          <Divider sx={{ mt: 2 }} />
        </Box>
      )}
      <Box
        sx={{
          borderRadius: 2,
          backgroundColor: "background.paper",
          boxShadow: (theme) => theme.shadows[cardElevation],
          p: variant === "tab" ? 0 : 1,
        }}
      >
        <ServerPaginatedTable {...logsTableProps} />
      </Box>
    </Box>
  );
};

export default ExitRequestLogs;
